
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, Loader2, X } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@/hooks/useAuth"
import { useProfiles } from "@/hooks/useProfiles"

interface FamilyMember {
  name: string
  relationship: string
}

interface Vehicle {
  brand: string
  model: string
  color: string
  plate: string
}

const NovaUnidadeModal = () => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  
  // Dados da unidade
  const [number, setNumber] = useState("")
  const [building, setBuilding] = useState("")
  const [floor, setFloor] = useState("")
  const [ownerId, setOwnerId] = useState("none")
  const [unitType, setUnitType] = useState("apartamento")
  
  // Dados do proprietário
  const [ownerName, setOwnerName] = useState("")
  const [ownerEmail, setOwnerEmail] = useState("")
  const [ownerPhone, setOwnerPhone] = useState("")
  
  // Dados do locatário
  const [tenantId, setTenantId] = useState("none")
  const [tenantName, setTenantName] = useState("")
  const [tenantEmail, setTenantEmail] = useState("")
  const [tenantPhone, setTenantPhone] = useState("")
  const [tenantStartDate, setTenantStartDate] = useState("")
  const [tenantEndDate, setTenantEndDate] = useState("")
  
  // Familiares
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([])
  const [newMemberName, setNewMemberName] = useState("")
  const [newMemberRelationship, setNewMemberRelationship] = useState("")
  
  // Veículos
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [newVehicleBrand, setNewVehicleBrand] = useState("")
  const [newVehicleModel, setNewVehicleModel] = useState("")
  const [newVehicleColor, setNewVehicleColor] = useState("")
  const [newVehiclePlate, setNewVehiclePlate] = useState("")
  
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { profile } = useAuth()
  const { data: profiles } = useProfiles()

  const addFamilyMember = () => {
    if (newMemberName && newMemberRelationship) {
      setFamilyMembers([...familyMembers, { 
        name: newMemberName, 
        relationship: newMemberRelationship 
      }])
      setNewMemberName("")
      setNewMemberRelationship("")
    }
  }

  const removeFamilyMember = (index: number) => {
    setFamilyMembers(familyMembers.filter((_, i) => i !== index))
  }

  const addVehicle = () => {
    if (newVehicleBrand && newVehicleModel && newVehicleColor && newVehiclePlate) {
      setVehicles([...vehicles, {
        brand: newVehicleBrand,
        model: newVehicleModel,
        color: newVehicleColor,
        plate: newVehiclePlate
      }])
      setNewVehicleBrand("")
      setNewVehicleModel("")
      setNewVehicleColor("")
      setNewVehiclePlate("")
    }
  }

  const removeVehicle = (index: number) => {
    setVehicles(vehicles.filter((_, i) => i !== index))
  }

  const resetForm = () => {
    setNumber("")
    setBuilding("")
    setFloor("")
    setOwnerId("none")
    setUnitType("apartamento")
    setOwnerName("")
    setOwnerEmail("")
    setOwnerPhone("")
    setTenantId("none")
    setTenantName("")
    setTenantEmail("")
    setTenantPhone("")
    setTenantStartDate("")
    setTenantEndDate("")
    setFamilyMembers([])
    setVehicles([])
    setNewMemberName("")
    setNewMemberRelationship("")
    setNewVehicleBrand("")
    setNewVehicleModel("")
    setNewVehicleColor("")
    setNewVehiclePlate("")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      console.log('Criando nova unidade')

      // Criar unidade
      const { data: unitData, error: unitError } = await supabase
        .from("units")
        .insert([{
          condominium_id: profile?.condominium_id,
          number,
          building: building || null,
          floor: floor ? parseInt(floor) : null,
          owner_id: ownerId === "none" ? null : ownerId,
          tenant_id: tenantId === "none" ? null : tenantId
        }])
        .select()
        .single()

      if (unitError) throw unitError

      // Criar detalhes da unidade se houver dados
      if (ownerName || ownerEmail || ownerPhone || tenantName || tenantEmail || tenantPhone || familyMembers.length > 0 || vehicles.length > 0) {
        const { error: detailsError } = await supabase
          .from("unit_details")
          .insert({
            unit_id: unitData.id,
            unit_type: unitType,
            owner_name: ownerName || null,
            owner_email: ownerEmail || null,
            owner_phone: ownerPhone || null,
            tenant_name: tenantName || null,
            tenant_email: tenantEmail || null,
            tenant_phone: tenantPhone || null,
            tenant_start_date: tenantStartDate || null,
            tenant_end_date: tenantEndDate || null,
            family_members: familyMembers as any,
            vehicles: vehicles as any
          })

        if (detailsError) throw detailsError
      }

      toast({
        title: "Sucesso!",
        description: "Unidade criada com sucesso.",
      })

      setOpen(false)
      resetForm()
      queryClient.invalidateQueries({ queryKey: ["units"] })
      queryClient.invalidateQueries({ queryKey: ["stats"] })
    } catch (error: any) {
      console.error("Erro ao criar unidade:", error)
      toast({
        title: "Erro",
        description: error.message || "Erro ao criar unidade.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Nova Unidade
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Unidade</DialogTitle>
          <DialogDescription>
            Cadastre uma nova unidade com todos os detalhes.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 py-4">
            {/* Dados da Unidade */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Dados da Unidade</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="unitType">Tipo *</Label>
                  <Select value={unitType} onValueChange={setUnitType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apartamento">Apartamento</SelectItem>
                      <SelectItem value="casa">Casa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="number">Número *</Label>
                  <Input
                    id="number"
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="building">Bloco</Label>
                  <Input
                    id="building"
                    value={building}
                    onChange={(e) => setBuilding(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="floor">Andar</Label>
                  <Input
                    id="floor"
                    type="number"
                    value={floor}
                    onChange={(e) => setFloor(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Dados do Proprietário */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Proprietário</h3>
              <div>
                <Label htmlFor="ownerName">Nome Completo</Label>
                <Input
                  id="ownerName"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ownerEmail">E-mail</Label>
                  <Input
                    id="ownerEmail"
                    type="email"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="ownerPhone">Telefone</Label>
                  <Input
                    id="ownerPhone"
                    value={ownerPhone}
                    onChange={(e) => setOwnerPhone(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="owner">Vincular Morador do Sistema</Label>
                <Select value={ownerId} onValueChange={setOwnerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um morador" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum morador</SelectItem>
                    {profiles?.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.full_name || profile.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dados do Locatário */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold">Locatário (Opcional)</h3>
              <div>
                <Label>Selecionar Locatário Existente</Label>
                <Select value={tenantId} onValueChange={setTenantId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sem locatário" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem locatário</SelectItem>
                    {profiles
                      ?.filter(p => p.role === 'morador' && p.condominium_id === profile?.condominium_id)
                      .map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tenantName">Nome do Locatário</Label>
                <Input
                  id="tenantName"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  placeholder="Nome completo"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tenantEmail">E-mail</Label>
                  <Input
                    id="tenantEmail"
                    type="email"
                    value={tenantEmail}
                    onChange={(e) => setTenantEmail(e.target.value)}
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div>
                  <Label htmlFor="tenantPhone">Telefone</Label>
                  <Input
                    id="tenantPhone"
                    value={tenantPhone}
                    onChange={(e) => setTenantPhone(e.target.value)}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tenantStartDate">Início do Contrato</Label>
                  <Input
                    id="tenantStartDate"
                    type="date"
                    value={tenantStartDate}
                    onChange={(e) => setTenantStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="tenantEndDate">Fim do Contrato</Label>
                  <Input
                    id="tenantEndDate"
                    type="date"
                    value={tenantEndDate}
                    onChange={(e) => setTenantEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Familiares */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Familiares</h3>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label htmlFor="memberName">Nome</Label>
                  <Input
                    id="memberName"
                    value={newMemberName}
                    onChange={(e) => setNewMemberName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="memberRelationship">Parentesco</Label>
                  <Select value={newMemberRelationship} onValueChange={setNewMemberRelationship}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="esposa">Esposa</SelectItem>
                      <SelectItem value="esposo">Esposo</SelectItem>
                      <SelectItem value="filho">Filho</SelectItem>
                      <SelectItem value="filha">Filha</SelectItem>
                      <SelectItem value="mae">Mãe</SelectItem>
                      <SelectItem value="pai">Pai</SelectItem>
                      <SelectItem value="irmao">Irmão</SelectItem>
                      <SelectItem value="irma">Irmã</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button type="button" onClick={addFamilyMember} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {familyMembers.length > 0 && (
                <div className="space-y-2">
                  {familyMembers.map((member, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span>{member.name} - {member.relationship}</span>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeFamilyMember(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Veículos */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Veículos</h3>
              <div className="grid grid-cols-5 gap-2">
                <div>
                  <Label htmlFor="vehicleBrand">Marca</Label>
                  <Input
                    id="vehicleBrand"
                    value={newVehicleBrand}
                    onChange={(e) => setNewVehicleBrand(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="vehicleModel">Modelo</Label>
                  <Input
                    id="vehicleModel"
                    value={newVehicleModel}
                    onChange={(e) => setNewVehicleModel(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="vehicleColor">Cor</Label>
                  <Input
                    id="vehicleColor"
                    value={newVehicleColor}
                    onChange={(e) => setNewVehicleColor(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="vehiclePlate">Placa</Label>
                  <Input
                    id="vehiclePlate"
                    value={newVehiclePlate}
                    onChange={(e) => setNewVehiclePlate(e.target.value.toUpperCase())}
                  />
                </div>
                <div className="flex items-end">
                  <Button type="button" onClick={addVehicle} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {vehicles.length > 0 && (
                <div className="space-y-2">
                  {vehicles.map((vehicle, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span>{vehicle.brand} {vehicle.model} - {vehicle.color} - {vehicle.plate}</span>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeVehicle(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Criar Unidade
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default NovaUnidadeModal
